import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md font-semibold transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-signalOrange-300 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-signalOrange-500 text-white hover:bg-signalOrange-600 disabled:bg-signalOrange-200',
        yellow:
          'bg-primary-500 text-charcoalGray-800 hover:bg-primary-600 disabled:bg-signalOrange-200 disabled:text-white',
        outline:
          'border border-charcoalGray-50 bg-white text-charcoalGray-800 hover:bg-charcoalGray-50 disabled:border-0 disabled:bg-signalOrange-200 disabled:text-white dark:border-charcoalGray-500 dark:bg-charcoalGray-600 dark:text-white dark:hover:bg-charcoalGray-500 dark:disabled:bg-signalOrange-200',
        link:
          'text-signalOrange-500 hover:bg-signalOrange-100 disabled:bg-signalOrange-200 disabled:text-white',
        destructive:
          'bg-red-600 text-white shadow-sm hover:bg-red-700 disabled:bg-red-300',
        ghost:
          'text-charcoalGray-800 hover:bg-charcoalGray-50 dark:text-white dark:hover:bg-charcoalGray-600',
      },
      size: {
        xs: 'h-[34px] gap-2 px-3 py-2 text-xs [&_svg]:size-4',
        sm: 'h-9 gap-2.5 px-3 py-2 text-sm [&_svg]:size-[18px]',
        md: 'h-10 gap-3 px-5 py-2.5 text-sm [&_svg]:size-[18px]',
        'icon-xs': 'size-7 p-1.5 [&_svg]:size-4',
        'icon-sm': 'size-9 p-2 [&_svg]:size-[18px]',
        'icon-md': 'size-[42px] p-3 [&_svg]:size-[18px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
export type { ButtonProps };
